import { computed, type ComputedRef } from 'vue'

import { email } from '@/lib/validation'

import { ONBOARDING_FORM_STEPS, type OnboardingFormStepKey } from '../lib/onboardingFormSteps'
import { isValidOnboardingPhone } from '../lib/onboardingPhoneValidation'
import type { OnboardingFormData } from '../lib/onboardingPrefill'
import { resolveUsStateCode } from '../lib/usStates'

type Translate = (key: string, ...args: unknown[]) => string

/**
 * `key` is the form-data key (also the `field-key` / `data-ai-field` on screen).
 * `invalidMessageKey` is shown instead of "required" when the field has a value
 * that fails format validation (e.g. a malformed email or phone number).
 */
type FieldCheck = { key: string; isFilled: boolean; labelKey: string; invalidMessageKey?: string }
type Area = { areaLabelKey: string; step: OnboardingFormStepKey; fields: FieldCheck[] }

export type MissingOnboardingField = { key: string; label: string }
export type MissingOnboardingArea = { areaLabel: string; fields: MissingOnboardingField[] }
export type OnboardingFormStepStatus = { filled: number; total: number; complete: boolean }

const filled = (value: unknown) => Boolean((value ?? '').toString().trim())
const validEmail = (value: unknown) =>
  email((value ?? '').toString().trim()) === true && filled(value)

const EMAIL_INVALID = 'labels.email-invalid-validation'
const PHONE_INVALID = 'labels.phone-invalid'

/**
 * The new onboarding requires several groups of fields to be filled in before
 * it can be finished. Returns what's missing grouped by the on-screen section
 * it belongs to, so callers can show one specific hint instead of several
 * generic ones across multiple failed attempts, plus a per-field error lookup
 * for inline validation.
 *
 * Backend gate (bottimmo `isOnboardingDataComplete` guard on the ONBOARDED
 * event): company name, street, zip, city, country, commission (except US),
 * primary + secondary color, logo (when editable), ambassador first/last name,
 * role, email, mobile, phone (except turkoglu) and portrait image. The team
 * picture is a frontend-only requirement. The website
 * is optional (agents may have none). Imprint, privacy and about links are
 * required there only when a website is set, and submit falls back to the
 * website URL for them (`legalLinkForSubmit`), so they are optional here too.
 * Contact person and the US "Brokerage & license" fields (state, brokerage
 * name, license number, license status) are frontend-only requirements.
 */
export function useOnboardingDataCompleteness(options: {
  formData: OnboardingFormData
  platform: ComputedRef<string | null>
  isLogoEditable: ComputedRef<boolean>
  isCommissionEditable: ComputedRef<boolean>
  isUsOnboarding: ComputedRef<boolean>
  t: Translate
}) {
  const { formData, platform, isLogoEditable, isCommissionEditable, isUsOnboarding, t } = options

  const textField = (key: keyof OnboardingFormData, labelKey: string): FieldCheck => ({
    key,
    isFilled: filled(formData[key]),
    labelKey,
  })
  const emailField = (key: keyof OnboardingFormData, labelKey: string): FieldCheck => ({
    key,
    isFilled: validEmail(formData[key]),
    labelKey,
    invalidMessageKey: EMAIL_INVALID,
  })
  const phoneField = (key: keyof OnboardingFormData, labelKey: string): FieldCheck => ({
    key,
    isFilled: isValidOnboardingPhone(formData[key]),
    labelKey,
    invalidMessageKey: PHONE_INVALID,
  })

  const areas = computed<Area[]>(() => [
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-name-website',
      step: 'company',
      fields: [
        textField(
          'name',
          isUsOnboarding.value
            ? 'onboarding.wizard.data-entry.us-company-name'
            : 'onboarding.wizard.basic-info.company-name'
        ),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-info',
      step: 'company',
      fields: [
        textField('street', 'onboarding.wizard.data-entry.street'),
        textField('zipcode', 'onboarding.wizard.data-entry.zipcode'),
        textField('city', 'onboarding.wizard.data-entry.city'),
        textField('country', 'onboarding.wizard.data-entry.country'),
      ],
    },
    ...(isUsOnboarding.value
      ? [
          {
            areaLabelKey: 'onboarding.wizard.data-entry.brokerage-license',
            step: 'company' as const,
            fields: [
              {
                key: 'state',
                // Only a recognised US state counts — free text from the scrape must be picked again.
                isFilled: Boolean(resolveUsStateCode(formData.state)),
                labelKey: 'onboarding.wizard.data-entry.state',
              },
              textField('brokerageName', 'onboarding.wizard.data-entry.us-brokerage-name'),
              textField('licenseNumber', 'onboarding.wizard.data-entry.us-license-number'),
              textField('licenseStatus', 'onboarding.wizard.data-entry.license-status'),
            ],
          },
        ]
      : []),
    {
      areaLabelKey: 'onboarding.wizard.data-entry.responsible-person',
      step: 'contacts',
      fields: [
        textField('contactFirstName', 'onboarding.wizard.data-entry.contact-first-name'),
        textField('contactLastName', 'onboarding.wizard.data-entry.contact-last-name'),
        emailField('contactEmail', 'onboarding.submit.fields.email'),
        phoneField('contactPhone', 'onboarding.wizard.data-entry.phone'),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.public-facing-ambassador',
      step: 'contacts',
      fields: [
        textField('ambassadorFirstName', 'onboarding.wizard.data-entry.contact-first-name'),
        textField('ambassadorLastName', 'onboarding.wizard.data-entry.contact-last-name'),
        textField('ambassadorRole', 'onboarding.submit.fields.role'),
        emailField('ambassadorEmail', 'onboarding.submit.fields.email'),
        phoneField('ambassadorMobile', 'onboarding.wizard.data-entry.mobile'),
        ...(platform.value !== 'turkoglu'
          ? [phoneField('phone', 'onboarding.wizard.data-entry.phone')]
          : []),
        // Hidden unless the company feature allows editing it, and never asked for US
        // companies (bottimmo's guard skips it for country "us").
        ...(isCommissionEditable.value && !isUsOnboarding.value
          ? [textField('commission', 'onboarding.wizard.data-entry.commission-rate')]
          : []),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.brand-colors',
      step: 'brand',
      fields: [
        textField('primaryColor', 'onboarding.wizard.data-entry.primary-color'),
        textField('secondaryColor', 'onboarding.wizard.data-entry.secondary-color'),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-images',
      step: 'brand',
      fields: [
        textField('teamPicture', 'settings.company.images.labels.team-picture'),
        textField('portraitImage', 'settings.company.images.labels.portrait-picture'),
        ...(isLogoEditable.value
          ? [textField('companyLogo', 'settings.company.images.labels.company-logo')]
          : []),
      ],
    },
  ])

  const toMissingAreas = (list: Area[]): MissingOnboardingArea[] =>
    list
      .map((area) => ({
        areaLabel: t(area.areaLabelKey),
        fields: area.fields
          .filter((field) => !field.isFilled)
          .map((field) => ({ key: field.key, label: t(field.labelKey) })),
      }))
      .filter((area) => area.fields.length > 0)

  const missingFieldsByArea = computed(() => toMissingAreas(areas.value))
  const missingFieldsForStep = (step: OnboardingFormStepKey) =>
    toMissingAreas(areas.value.filter((area) => area.step === step))

  /** Required-field progress per form step; steps without required fields are complete. */
  const stepStatus = computed(() => {
    const status = Object.fromEntries(
      ONBOARDING_FORM_STEPS.map(({ key }) => [key, { filled: 0, total: 0, complete: true }])
    ) as Record<OnboardingFormStepKey, OnboardingFormStepStatus>
    for (const area of areas.value) {
      const entry = status[area.step]
      for (const field of area.fields) {
        entry.total += 1
        if (field.isFilled) entry.filled += 1
      }
      entry.complete = entry.filled === entry.total
    }
    return status
  })

  const requiredProgress = computed(() =>
    Object.values(stepStatus.value).reduce(
      (sum, { filled, total }) => ({ filled: sum.filled + filled, total: sum.total + total }),
      { filled: 0, total: 0 }
    )
  )

  const hasMissingOnboardingData = computed(() => missingFieldsByArea.value.length > 0)

  const missingOnboardingDataSummary = computed(() =>
    missingFieldsByArea.value
      .map((area) => `${area.areaLabel}: ${area.fields.map((field) => field.label).join(', ')}`)
      .join('\n')
  )

  /** Required fields that are filled but malformed (e.g. a phone without a valid number). */
  const invalidRequiredFields = computed<MissingOnboardingField[]>(() =>
    areas.value.flatMap((area) =>
      area.fields
        .filter(
          (field) =>
            !field.isFilled &&
            field.invalidMessageKey &&
            filled(formData[field.key as keyof OnboardingFormData])
        )
        .map((field) => ({ key: field.key, label: t(field.labelKey) }))
    )
  )

  /** Inline error for a field, or `''` when it is filled/valid or not required. */
  const fieldErrors = computed(() => {
    const errors: Record<string, string> = {}
    for (const area of areas.value) {
      for (const field of area.fields) {
        if (field.isFilled) continue
        const hasValue = filled(formData[field.key as keyof OnboardingFormData])
        errors[field.key] = t(
          hasValue && field.invalidMessageKey ? field.invalidMessageKey : 'labels.required'
        )
      }
    }
    return errors
  })
  const fieldError = (key: string) => fieldErrors.value[key] ?? ''

  const requiredFieldKeys = computed(
    () => new Set(areas.value.flatMap((area) => area.fields.map((field) => field.key)))
  )
  const isFieldRequired = (key: string) => requiredFieldKeys.value.has(key)

  return {
    hasMissingOnboardingData,
    missingOnboardingDataSummary,
    missingFieldsByArea,
    missingFieldsForStep,
    stepStatus,
    requiredProgress,
    fieldError,
    isFieldRequired,
    invalidRequiredFields,
  }
}
